/**
 * Panel styles, injected once per document.
 *
 * Why an injected stylesheet rather than utility classes or inline styles:
 *
 *  - This package ships into EXTERNAL plugin iframes (as a vendored library —
 *    see `docs/panels.md`), and plugins have their own Tailwind build. Utility
 *    classes would only work if every consumer configured their content
 *    scanner to include this package — a silent "styles just don't appear"
 *    failure the first time someone forgets.
 *  - Inline styles cannot express `:hover`, `:focus-visible` or
 *    `prefers-reduced-motion`, and focus visibility is an accessibility
 *    requirement, not a nicety.
 *
 * Theming is by CSS custom property with a sane fallback, so a panel looks
 * correct with zero configuration and correct-for-the-brand once a host sets the
 * variables. Defaults use the CSS system colors (`Canvas` / `CanvasText`), which
 * follow `color-scheme`, so light/dark works out of the box.
 *
 * Injection targets the panel's OWN document (`ownerDocument`), not the global
 * `document`: inside a plugin the panel lives in an iframe, and appending to the
 * parent document would style nothing.
 */

export const PANEL_STYLE_ELEMENT_ID = 'fias-panel-kit-styles';

export const PANEL_STYLES = `
.fias-panel {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: var(--fias-panel-radius, 12px);
  background: var(--fias-panel-bg, Canvas);
  color: var(--fias-panel-fg, CanvasText);
  border: 1px solid var(--fias-panel-border, rgba(127, 127, 127, 0.35));
  font-family: var(--fias-panel-font, inherit);
  text-align: center;
}
.fias-panel *,
.fias-panel *::before,
.fias-panel *::after { box-sizing: border-box; }
.fias-panel__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
}
.fias-panel__text { margin: 0; line-height: 1.5; overflow-wrap: anywhere; }
.fias-panel__text--title { font-size: 1.0625rem; font-weight: 600; }
.fias-panel__text--body { font-size: 0.9375rem; }
.fias-panel__text--caption { font-size: 0.8125rem; opacity: 0.75; }
.fias-panel__text--left { text-align: left; }
.fias-panel__text--center { text-align: center; }
.fias-panel__text--right { text-align: right; }
.fias-panel__image { display: block; margin: 0 auto; max-width: 100%; height: auto; }
.fias-panel__items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.fias-panel__item { display: flex; align-items: center; gap: 6px; font-size: 0.875rem; }
.fias-panel__item-icon { display: block; object-fit: contain; }
.fias-panel__item-icon--small { width: 20px; height: 20px; }
.fias-panel__item-icon--medium { width: 32px; height: 32px; }
.fias-panel__item-icon--large { width: 48px; height: 48px; }
.fias-panel__item-amount { font-variant-numeric: tabular-nums; opacity: 0.85; }
.fias-panel__items-empty { margin: 0; font-size: 0.8125rem; opacity: 0.75; }
.fias-panel__spacer--small { height: 4px; }
.fias-panel__spacer--medium { height: 12px; }
.fias-panel__spacer--large { height: 24px; }
.fias-panel__buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}
.fias-panel__button {
  appearance: none;
  cursor: pointer;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: var(--fias-panel-button-radius, 8px);
  border: 1px solid transparent;
  transition: filter 120ms ease, background-color 120ms ease;
}
.fias-panel__button:disabled { cursor: default; opacity: 0.5; }
.fias-panel__button--primary {
  background: var(--fias-panel-accent, #2563eb);
  color: var(--fias-panel-accent-fg, #ffffff);
}
.fias-panel__button--secondary {
  background: transparent;
  color: inherit;
  border-color: var(--fias-panel-border, rgba(127, 127, 127, 0.35));
}
.fias-panel__button:not(:disabled):hover { filter: brightness(1.08); }
.fias-panel__button:focus-visible,
.fias-panel__close:focus-visible {
  outline: 2px solid var(--fias-panel-accent, #2563eb);
  outline-offset: 2px;
}
.fias-panel__close {
  position: absolute;
  top: 8px;
  right: 8px;
  appearance: none;
  cursor: pointer;
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  line-height: 1;
  font-size: 1.25rem;
  padding: 4px 8px;
  border-radius: 6px;
  opacity: 0.7;
}
.fias-panel__close:hover { opacity: 1; }
/* Visually hidden but announced — the close button's accessible name. */
.fias-panel__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.fias-panel-scrim {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--fias-panel-scrim, rgba(0, 0, 0, 0.5));
  z-index: var(--fias-panel-z, 1000);
  overflow-y: auto;
}
.fias-panel-scrim .fias-panel {
  position: relative;
  width: 100%;
  margin: auto;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  animation: fias-panel-in 140ms ease-out;
}
.fias-panel-scrim .fias-panel--small { max-width: 320px; }
.fias-panel-scrim .fias-panel--medium { max-width: 440px; }
.fias-panel-scrim .fias-panel--large { max-width: 640px; }

@keyframes fias-panel-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .fias-panel-scrim .fias-panel { animation: none; }
  .fias-panel__button { transition: none; }
}
`;

/**
 * Ensure the stylesheet exists in `doc`. Idempotent and cheap — safe to call
 * from every panel's mount effect.
 */
export function ensurePanelStyles(doc: Document | null | undefined): void {
  if (!doc?.head) return;
  if (doc.getElementById(PANEL_STYLE_ELEMENT_ID)) return;
  const style = doc.createElement('style');
  style.id = PANEL_STYLE_ELEMENT_ID;
  style.textContent = PANEL_STYLES;
  doc.head.appendChild(style);
}
